// Puerto Rico timezone utilities
// Puerto Rico uses Atlantic Standard Time (AST) UTC-4 year-round (no daylight saving)

const PR_TIMEZONE = 'America/Puerto_Rico';

/**
 * Format a date to Puerto Rico timezone
 * @param {Date|string} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDatePR = (date, options = {}) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const defaultOptions = {
    timeZone: PR_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options
  };
  
  return new Intl.DateTimeFormat('es-PR', defaultOptions).format(dateObj);
};

/**
 * Format a date and time to Puerto Rico timezone
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTimePR = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat('es-PR', {
    timeZone: PR_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(dateObj);
};

/**
 * Format time only in Puerto Rico timezone
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted time string
 */
export const formatTimePR = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat('es-PR', {
    timeZone: PR_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(dateObj);
};

/**
 * Get current date/time in Puerto Rico
 * @returns {Date} Current date in PR timezone context
 */
export const getCurrentTimePR = () => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: PR_TIMEZONE }));
};

/**
 * Format relative time (e.g., "hace 2 horas")
 * @param {Date|string} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'ahora mismo';
  if (diffMins < 60) return `hace ${diffMins} min`;
  if (diffHours < 24) return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  if (diffDays < 7) return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
  
  return formatDatePR(dateObj);
};

export default {
  formatDatePR,
  formatDateTimePR,
  formatTimePR,
  getCurrentTimePR,
  formatRelativeTime,
  PR_TIMEZONE
};
